import { useEffect, useRef, useState, useCallback } from 'react';
import { useMedia } from 'react-use';
import TopBar from './components/topBar/TopBar';
import OrderBook from './components/orderBook/OrderBook';
import TradeForm from './components/TradeSection/TradeForm';
import MyHistoryView from './components/historyView/MyHistoryView';
import { MarketChart } from './MarketChart';
import { useDarkMode, useLayoutSwap } from '../../recoil';
import type { ColumnComponent, CenterSubComponent } from '../../recoil';
import SwapButton from './components/SwapButton';

const TradePage: React.FC<any> = () => {
  const [darkMode, setDarkMode] = useDarkMode();
  const isNotMobile = useMedia('(min-width:1200px)');
  const {
    positions,
    centerOrder,
    swapLeftCenter,
    swapRightCenter,
    swapCenterTopBottom,
  } = useLayoutSwap();

  const [leftComponent, centerComponent, rightComponent] = positions;

  const [spaceHeight, setSpaceHeight] = useState(200);
  const [isDragging, setIsDragging] = useState(false);

  const dragStartY = useRef(0);
  const dragStartHeight = useRef(0);

  const onMouseMove = useCallback(
    (moveEvent: MouseEvent) => {
      if (!isDragging) return;
      const deltaY = moveEvent.clientY - dragStartY.current;
      const newHeight = Math.max(
        Math.min(dragStartHeight.current - deltaY, 500),
        50
      );
      setSpaceHeight(newHeight);
    },
    [isDragging]
  );

  const onMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      return () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
    }
  }, [isDragging, onMouseMove, onMouseUp]);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragStartY.current = e.clientY;
    dragStartHeight.current = spaceHeight;
    setIsDragging(true);
  };

  const [activeTab, setActiveTab] = useState<'trade' | 'history' | 'orderbook'>(
    'trade'
  );

  const renderCenterSub = (sub: CenterSubComponent) => {
    if (sub === 'chart') {
      return isDragging ? (
        <div className="flex items-center justify-center h-full dark:text-white dark:bg-cardColor bg-[#f1f1f1] rounded-lg">
          <div className="text-center">
            <p className="text-f27 text-gray-600">Adjusting chart...</p>
          </div>
        </div>
      ) : (
        <div className="dark:bg-cardColor bg-[#f1f1f1] rounded-lg h-full flex flex-col flex-grow p-3 gap-2">
          <MarketChart />
        </div>
      );
    }
    return <MyHistoryView />;
  };

  const renderChartHistory = () => {
    const [topComponent, bottomComponent] = centerOrder;

    return (
      <div className="flex flex-col flex-grow h-full">
        {/* Top section */}
        <div className="flex flex-col flex-grow">
          {renderCenterSub(topComponent)}
        </div>

        {/* Draggable Divider */}
        <div
          style={{
            height: '12px',
            cursor: 'row-resize',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
          onMouseDown={onMouseDown}
        >
          {/* Drag handle dots */}
          <div style={{ display: 'flex', gap: '2px' }}>
            <div
              style={{
                width: '3px',
                height: '3px',
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                borderRadius: '50%',
              }}
            />
            <div
              style={{
                width: '3px',
                height: '3px',
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                borderRadius: '50%',
              }}
            />
            <div
              style={{
                width: '3px',
                height: '3px',
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                borderRadius: '50%',
              }}
            />
          </div>
          {/* Vertical swap button */}
          <div className="absolute right-2" style={{ top: '-4px' }}>
            <SwapButton onClick={swapCenterTopBottom} direction="vertical" />
          </div>
        </div>

        {/* Bottom section (adjustable height) */}
        <div
          style={{
            height: `${spaceHeight}px`,
            transition: isDragging ? 'none' : 'height 0.1s ease-out',
            overflow: 'hidden',
          }}
          className="pb-2 pt-0"
        >
          {renderCenterSub(bottomComponent)}
        </div>
      </div>
    );
  };

  const renderComponent = (component: ColumnComponent) => {
    switch (component) {
      case 'orderbook':
        return <OrderBook />;
      case 'tradeform':
        return <TradeForm />;
      case 'chart-history':
        return renderChartHistory();
    }
  };

  return (
    <div className="flex h-full justify-between w-full  font-sans">
      <div className={`w-full h-full ${darkMode ? ' bg-black ' : 'bg-white'}`}>
        <TopBar isNotMobile={isNotMobile} />
        {isNotMobile ? (
          <div className="flex h-[calc(100%-65px)]">
            {/* Left Column */}
            <div className={`h-full shrink-0 ${leftComponent === 'chart-history' ? 'flex flex-col flex-grow min-w-0' : ''}`}>
              {renderComponent(leftComponent)}
            </div>

            {/* Swap Button: Left <-> Center */}
            <div className="flex items-center justify-center relative" style={{ width: '0px' }}>
              <SwapButton
                onClick={swapLeftCenter}
                direction="horizontal"
                className="absolute z-20"
              />
            </div>

            {/* Center Column */}
            <div className={`h-full shrink-0 ${centerComponent === 'chart-history' ? 'flex flex-col flex-grow min-w-0' : ''}`}>
              {renderComponent(centerComponent)}
            </div>

            {/* Swap Button: Center <-> Right */}
            <div className="flex items-center justify-center relative" style={{ width: '0px' }}>
              <SwapButton
                onClick={swapRightCenter}
                direction="horizontal"
                className="absolute z-20"
              />
            </div>

            {/* Right Column */}
            <div className={`h-full shrink-0 ${rightComponent === 'chart-history' ? 'flex flex-col flex-grow min-w-0' : ''}`}>
              {renderComponent(rightComponent)}
            </div>
          </div>
        ) : (
          <div className="flex flex-col flex-grow">
            <div className=" h-[76vh]">
              <div className="flex flex-col flex-grow bg-cardColor rounded-lg p-3 mb-2 h-full">
                <MarketChart />
              </div>
            </div>
            {/* Tabs for mobile */}
            <div className="flex justify-around bg-black p-2 text-white text-[12px]">
              <button
                onClick={() => setActiveTab('trade')}
                className={`p-2  ${
                  activeTab === 'trade'
                    ? 'text-brand border-b-2 border-brand'
                    : 'text-gray-500'
                }`}
              >
                Trade
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`p-2  ${
                  activeTab === 'history'
                    ? 'text-brand border-b-2 border-brand'
                    : 'text-gray-500'
                }`}
              >
                History
              </button>
              <button
                onClick={() => setActiveTab('orderbook')}
                className={`p-2  ${
                  activeTab === 'orderbook'
                    ? 'text-brand border-b-2 border-brand'
                    : 'text-gray-500'
                }`}
              >
                Order Book
              </button>
            </div>
            <div className="flex-grow bg-cardColor rounded-lg p-3">
              {activeTab === 'trade' && <TradeForm isNotMobile={isNotMobile} />}
              {activeTab === 'history' && (
                <MyHistoryView isNotMobile={isNotMobile} />
              )}
              {activeTab === 'orderbook' && (
                <OrderBook isNotMobile={isNotMobile} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export { TradePage };
export default TradePage;
